import { useEffect, useState, useCallback } from 'react'
import * as Icons from 'lucide-react'
import {
  MonitorPlay, Plus, Trash2, Edit3, Ban, ExternalLink, Copy, Check, Wifi, WifiOff,
  RotateCw, Save, Trash2 as Trash, Settings, LayoutGrid, Grid3x3,
  ChevronDown, ChevronRight, Users, Shield,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import PageHeader from '@/components/PageHeader'
import { screensAPI } from '@/lib/api'
import { useScreensSocket } from '@/lib/screensSocket'
import ScreenMidiPanel, { useScreenMidi } from '@/components/ScreenMidiPanel'
import CustomBoardsCard from '@/components/CustomBoardsCard'
import OverlayChannelsPanel from '@/components/OverlayChannelsPanel'
import OBSInstancesPanel from '@/components/OBSInstancesPanel'
import { presetKleur, schermKleur } from '@/lib/screenColors'
import { LED_COLORS } from '@/lib/tournamentsMidi'
import ColorPicker from '@/components/ColorPicker'
import LedColorPicker from '@/components/LedColorPicker'
import ButtonLedPicker from '@/components/ButtonLedPicker'

/* Zwart is geen preset — het is een expliciete "uit"-actie per scherm. */
const ZWART = { route: null, label: 'Zwart', icon_name: 'Ban', led_color: 'white' }

/** Lucide-icoon opzoeken op naam, met een veilige fallback. */
function icoonVan(naam) { return Icons[naam] || Icons.MonitorPlay }

/** Preset → objecten met resolved icon-component en effectieve UI-kleur. */
function verrijk(preset) {
  return {
    ...preset,
    Icon: icoonVan(preset.icon_name),
    color: presetKleur(preset),
  }
}

export default function ScreensControlPage() {
  const [screens, setScreens] = useState([])
  const [presets, setPresets] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog]   = useState(null)
  const [presetDlg, setPresetDlg] = useState(null)
  const [sceneDlg, setSceneDlg]   = useState(null)
  const [error, setError]     = useState(null)
  const [copied, setCopied]   = useState(null)
  const [scenes, setScenes]   = useState([])
  const [zwartVan, setZwartVan] = useState({})

  const load = useCallback(() => {
    screensAPI.list()
      .then(r => setScreens(r.data?.screens ?? []))
      .catch(e => setError(e.response?.data?.error ?? e.message))
      .finally(() => setLoading(false))
    screensAPI.listPresets()
      .then(r => setPresets((r.data?.presets ?? []).map(verrijk)))
      .catch(() => {})
    screensAPI.listScenes()
      .then(r => setScenes(r.data?.scenes ?? []))
      .catch(() => {})
  }, [])

  useEffect(() => { load() }, [load])
  useScreensSocket(load)
  useEffect(() => { const t = setInterval(load, 20_000); return () => clearInterval(t) }, [load])

  const setRoute = async (id, route) => {
    const scherm = screens.find(s => s.id === id)
    let doel = route
    if (route != null && scherm?.current_route === route) {
      doel = null
      setZwartVan(v => ({ ...v, [id]: route }))
    } else {
      setZwartVan(v => { const n = { ...v }; delete n[id]; return n })
    }
    try { await screensAPI.setRoute(id, doel); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const pasSceneToe = async (id) => {
    try { await screensAPI.applyScene(id); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const legVast = async () => {
    const naam = window.prompt('Naam voor deze stand?')
    if (!naam?.trim()) return
    const knop = window.prompt('Op welke scene-knop (1-8)? Leeg laten mag.')
    try {
      await screensAPI.captureScene({ name: naam.trim(), midi_slot: knop?.trim() || null })
      load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const wisScene = async (id) => {
    if (!window.confirm('Deze scene verwijderen?')) return
    try { await screensAPI.deleteScene(id); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const bewaarScene = async (e) => {
    e.preventDefault()
    try {
      await screensAPI.updateScene(sceneDlg.id, {
        name: sceneDlg.name,
        midi_slot: sceneDlg.midi_slot === '' ? null : Number(sceneDlg.midi_slot),
        led_color: sceneDlg.led_color || 'yellow',
        blink: !!sceneDlg.blink,
      })
      setSceneDlg(null); load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const zetRotatie = async (scherm, aan, rij = null) => {
    try {
      // Bij aanzetten: pak alleen de presets die op DEZE rij zitten (2D-matrix).
      // Zo blijft rij 1's autoplay bij rij-1-presets, rij 2 bij rij-2-presets,
      // en spring je niet tussen inhoud van andere rijen.
      const kind = scherm.kind || 'participant'
      const bijPassend = presets.filter(p => {
        if (p.kind !== kind) return false
        if (rij === null) return true
        const rows = Array.isArray(p.midi_rows) ? p.midi_rows : []
        return rows.includes(rij)
      })
      const routes = bijPassend
        .slice()
        .sort((a, b) => (a.midi_col ?? 99) - (b.midi_col ?? 99))
        .map(p => p.route)
        .filter(Boolean)
      const body = aan ? { routes, active: true } : { active: false }
      await screensAPI.setRotation(scherm.id, body)
      load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const zetInterval = async (scherm, seconden) => {
    try { await screensAPI.setRotation(scherm.id, { seconds: seconden }); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const zetKind = async (scherm, kind) => {
    try { await screensAPI.update(scherm.id, { kind }); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const zetRij = async (scherm, rowsOrValue) => {
    // 2D-matrix: `rowsOrValue` is nu een array (0..7). Legacy call met single
    // value/string wordt naar array gemapt.
    const rows = Array.isArray(rowsOrValue)
      ? rowsOrValue
      : (rowsOrValue === '' || rowsOrValue == null ? [] : [Number(rowsOrValue)])
    try { await screensAPI.update(scherm.id, { midi_rows: rows }); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const zetPresetRijen = async (preset, rows) => {
    try { await screensAPI.updatePreset(preset.id, { midi_rows: rows }); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const zetKolom = async (preset, col) => {
    try {
      await screensAPI.updatePreset(preset.id, { midi_col: col === '' ? null : Number(col) })
      load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const wisPreset = async (id) => {
    if (!window.confirm('Deze inhoud verwijderen? Schermen die er nu op staan worden zwart.')) return
    try { await screensAPI.deletePreset(id); load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const bewaarPreset = async (e) => {
    e.preventDefault()
    const body = {
      route: presetDlg.route, label: presetDlg.label,
      icon_name: presetDlg.icon_name || 'MonitorPlay',
      led_color: presetDlg.led_color || 'white',
      // Bij deze picker sturen LED én UI samen op — dus expliciet leeg.
      ui_color: '',
      kind: presetDlg.kind || 'participant',
      midi_col: presetDlg.midi_col === '' ? null : Number(presetDlg.midi_col),
    }
    try {
      if (presetDlg.id) await screensAPI.updatePreset(presetDlg.id, body)
      else              await screensAPI.createPreset(body)
      setPresetDlg(null); load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const screensMetZwart = screens.map(s => ({ ...s, _blackRoute: zwartVan[s.id] ?? null }))

  const midi = useScreenMidi({
    screens: screensMetZwart, presets, scenes,
    onSetRoute: setRoute,
    onApplyScene: pasSceneToe,
    onToggleRotation: zetRotatie,
    onSetInterval: zetInterval,
  })

  const save = async (e) => {
    e.preventDefault()
    setError(null)
    try {
      if (dialog.id) await screensAPI.update(dialog.id, {
        name: dialog.name, note: dialog.note, kind: dialog.kind, ui_color: dialog.ui_color ?? '',
      })
      else           await screensAPI.create({ slug: dialog.slug, name: dialog.name, note: dialog.note })
      setDialog(null); load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const del = async (id) => {
    if (!window.confirm('Dit scherm uit de regie verwijderen?')) return
    await screensAPI.remove(id); load()
  }

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/scherm/${slug}`
    navigator.clipboard?.writeText(url)
    setCopied(slug); setTimeout(() => setCopied(null), 1500)
  }

  const deelnemerPresets = presets.filter(p => p.kind === 'participant')
  const adminPresets     = presets.filter(p => p.kind === 'admin')

  return (
    <div>
      <PageHeader icon={MonitorPlay} title="Schermregie"
                  subtitle="Bepaal wat elke beamer en TV toont — vanaf deze ene pagina" />

      {error && <p className="text-sm text-signal mb-3">{error}</p>}

      {/* Scenes */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap mb-2">
            <div className="flex-1 min-w-[14rem]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Scenes
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Zet de hele zaal in één klik terug in een stand die je eerder vastlegde.
                Een scene met een knopnummer zit ook op de scene-knoppen van de APC.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={legVast} disabled={screens.length === 0}>
              <Save className="h-4 w-4 mr-1.5" /> Huidige stand opslaan
            </Button>
          </div>

          {scenes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nog geen scenes.</p>
          ) : (
            <div className="flex gap-2 flex-wrap">
              {scenes.map(sc => {
                const swatch = LED_COLORS[sc.led_color]?.swatch || '#E4C265'
                const blinkt = !!sc.blink
                return (
                  <div key={sc.id}
                       className="inline-flex items-center gap-2 rounded-[4px] border border-border bg-card pl-3 pr-1 py-1"
                       style={sc.midi_slot ? { borderLeft: `2px solid ${swatch}` } : undefined}>
                    {sc.midi_slot && (
                      <span className="font-mono text-[10px]" style={{ color: swatch }}>
                        {sc.midi_slot}{blinkt ? '·' : ''}
                      </span>
                    )}
                    <button onClick={() => pasSceneToe(sc.id)} className="text-sm hover:text-acid transition-colors">
                      {sc.name}
                    </button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                            onClick={() => setSceneDlg({
                              id: sc.id, name: sc.name,
                              midi_slot: sc.midi_slot ?? '',
                              led_color: sc.led_color || 'yellow',
                              blink: !!sc.blink,
                            })}>
                      <Edit3 className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => wisScene(sc.id)}>
                      <Trash className="h-3 w-3 text-signal" />
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ScreenMidiPanel midi={midi} screens={screensMetZwart} presets={presets} scenes={scenes} />

      {/* Schermen — rij-toewijzing + directe knoppen */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Grid3x3 className="h-4 w-4" /> Schermen ({screens.length})
          </CardTitle>
          <Button size="sm" onClick={() => setDialog({ slug: '', name: '', note: '', kind: 'participant' })}>
            <Plus className="h-4 w-4 mr-1" /> Scherm toevoegen
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-muted-foreground">Laden…</p>}
          {!loading && screens.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nog geen schermen. Voeg er een toe en open <code className="font-mono text-acid">/scherm/&lt;slug&gt;</code> op die beamer.
            </p>
          )}
          {(() => {
            const deelnemer = screens.filter(s => (s.kind || 'participant') === 'participant')
            const admin     = screens.filter(s => s.kind === 'admin')
            const renderRow = (s) => {
              const eigenPresets = presets.filter(p => p.kind === (s.kind || 'participant'))
              return (
                <ScreenRow
                  key={s.id} screen={s} presets={eigenPresets}
                  onSetRoute={setRoute}
                  onToggleRotation={zetRotatie}
                  onKind={zetKind} onRow={zetRij}
                  onEdit={() => setDialog({ id: s.id, slug: s.slug, name: s.name, note: s.note || '',
                                            kind: s.kind || 'participant', ui_color: s.ui_color || '' })}
                  onDelete={() => del(s.id)}
                  onCopy={() => copyUrl(s.slug)}
                  copied={copied === s.slug}
                />
              )
            }
            return (
              <>
                {deelnemer.length > 0 && (
                  <SchermGroep icon={Users} kleur="#61E294"
                               titel="Deelnemer-schermen"
                               ondertitel="Voor deelnemers in de zaal · programma, tornooien, sponsors"
                               aantal={deelnemer.length}>
                    {deelnemer.map(renderRow)}
                  </SchermGroep>
                )}
                {admin.length > 0 && (
                  <SchermGroep icon={Shield} kleur="#FFB57A"
                               titel="Admin-schermen"
                               ondertitel="Voor admin/regie · health, containers, live-cijfers"
                               aantal={admin.length}
                               extraTop={deelnemer.length > 0}>
                    {admin.map(renderRow)}
                  </SchermGroep>
                )}
              </>
            )
          })()}
        </CardContent>
      </Card>

      {/* Configuratie onderaan — inklapbaar zodat het niet in de weg zit tijdens de LAN. */}
      <div className="mt-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 px-1">
          Configuratie
        </div>

        <CustomBoardsCard onWijziging={load} />

        <OverlayChannelsPanel />
        <OBSInstancesPanel />

        <InklapbareKaart
          titel="Inhoud & APC-kolommen"
          icon={LayoutGrid}
          badge={`${presets.length}`}
          actie={
            <Button size="sm" onClick={() => setPresetDlg({ label: '', route: '', icon_name: 'MonitorPlay',
                                                            led_color: 'white', ui_color: '', kind: 'participant', midi_col: '' })}>
              <Plus className="h-4 w-4 mr-1" /> Inhoud toevoegen
            </Button>
          }
        >
          <PresetGroep titel="Voor deelnemer-schermen" icon={Users} kleur="#61E294"
                       ondertitel="Zichtbaar in de zaal — kies deze bij een deelnemer-scherm"
                       presets={deelnemerPresets}
                       onKolom={zetKolom} onRijen={zetPresetRijen} onEdit={setPresetDlg} onWis={wisPreset} />
          <PresetGroep titel="Voor admin-schermen" icon={Shield} kleur="#FFB57A"
                       ondertitel="Alleen zichtbaar op admin/regie-schermen"
                       presets={adminPresets} extraTop
                       onKolom={zetKolom} onRijen={zetPresetRijen} onEdit={setPresetDlg} onWis={wisPreset} />
        </InklapbareKaart>
      </div>

      {dialog && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{dialog.id ? 'Scherm bewerken' : 'Nieuw scherm'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div>
                <Label>Naam *</Label>
                <Input value={dialog.name} required
                       onChange={e => setDialog({ ...dialog, name: e.target.value })}
                       placeholder="Beamer links" />
              </div>
              {!dialog.id && (
                <div>
                  <Label>Slug *</Label>
                  <Input value={dialog.slug} required
                         onChange={e => setDialog({ ...dialog, slug: e.target.value })}
                         placeholder="beamer-links" />
                  <p className="text-xs text-muted-foreground mt-1">
                    Wordt het adres: <code className="font-mono">/scherm/{dialog.slug || '…'}</code>
                  </p>
                </div>
              )}
              <div>
                <Label>Soort</Label>
                <select value={dialog.kind ?? 'participant'}
                        onChange={e => setDialog({ ...dialog, kind: e.target.value })}
                        className={selectCls}>
                  <option value="participant">Deelnemer-scherm (voor de zaal)</option>
                  <option value="admin">Admin-scherm (server- en LAN-info)</option>
                </select>
              </div>
              {dialog.id && (
                <ColorPicker
                  label="Eigen kleur (accent — leeg = standaard voor dit soort)"
                  value={dialog.ui_color}
                  defaultColor={dialog.kind === 'admin' ? '#00d9ff' : '#B4FF39'}
                  onChange={c => setDialog({ ...dialog, ui_color: c })}
                />
              )}
              <div>
                <Label>Notitie</Label>
                <Input value={dialog.note}
                       onChange={e => setDialog({ ...dialog, note: e.target.value })}
                       placeholder="Hangt boven de bar" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialog(null)}>Annuleer</Button>
                <Button type="submit">{dialog.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {presetDlg && (
        <Dialog open onOpenChange={(o) => !o && setPresetDlg(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{presetDlg.id ? 'Inhoud bewerken' : 'Nieuwe inhoud'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={bewaarPreset} className="space-y-3">
              <div>
                <Label>Label *</Label>
                <Input value={presetDlg.label} required
                       onChange={e => setPresetDlg({ ...presetDlg, label: e.target.value })}
                       placeholder="Nu & straks" />
              </div>
              <div>
                <Label>Route *</Label>
                <Input value={presetDlg.route} required disabled={!!presetDlg.id}
                       onChange={e => setPresetDlg({ ...presetDlg, route: e.target.value })}
                       placeholder="/zaal/…  of  /scherm-admin/…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Soort</Label>
                  <select value={presetDlg.kind}
                          onChange={e => setPresetDlg({ ...presetDlg, kind: e.target.value })}
                          className={selectCls}>
                    <option value="participant">Deelnemer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <Label>APC-kolom</Label>
                  <select value={presetDlg.midi_col ?? ''}
                          onChange={e => setPresetDlg({ ...presetDlg, midi_col: e.target.value })}
                          className={selectCls}>
                    <option value="">Geen</option>
                    {[0,1,2,3,4,5,6,7].map(n => <option key={n} value={n}>Kolom {n + 1}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Icoon (lucide-naam)</Label>
                <Input value={presetDlg.icon_name}
                       onChange={e => setPresetDlg({ ...presetDlg, icon_name: e.target.value })}
                       placeholder="Radio" />
              </div>
              <LedColorPicker
                label="Kleur — één keuze voor knop, header én pad-LED"
                value={presetDlg.led_color}
                onChange={c => setPresetDlg({ ...presetDlg, led_color: c })}
              />
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setPresetDlg(null)}>Annuleer</Button>
                <Button type="submit">{presetDlg.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {sceneDlg && (
        <Dialog open onOpenChange={(o) => !o && setSceneDlg(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Scene bewerken</DialogTitle>
            </DialogHeader>
            <form onSubmit={bewaarScene} className="space-y-3">
              <div>
                <Label>Naam *</Label>
                <Input value={sceneDlg.name} required
                       onChange={e => setSceneDlg({ ...sceneDlg, name: e.target.value })} />
              </div>
              <div>
                <Label>Scene-knop op APC</Label>
                <select value={sceneDlg.midi_slot ?? ''}
                        onChange={e => setSceneDlg({ ...sceneDlg, midi_slot: e.target.value })}
                        className={selectCls}>
                  <option value="">Geen knop</option>
                  {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>Knop {n}</option>)}
                </select>
              </div>
              <LedColorPicker
                label="Kleur — voor de scene-badge in de regie"
                value={sceneDlg.led_color}
                onChange={c => setSceneDlg({ ...sceneDlg, led_color: c })}
              />
              <div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!sceneDlg.blink}
                         onChange={e => setSceneDlg({ ...sceneDlg, blink: e.target.checked })} />
                  Laat de scene-knop <strong>knipperen</strong> op de APC
                </label>
                <p className="text-[10px] text-muted-foreground mt-1">
                  De scene-LED op de mk2 is hardware-groen — de kleur hierboven verandert
                  alleen de badge in de regie, niet de knop zelf. Solid/knipperen wél.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setSceneDlg(null)}>Annuleer</Button>
                <Button type="submit">Opslaan</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

const selectCls = 'w-full rounded-[4px] border border-border bg-background px-2 py-1.5 text-sm'

/* Kaart met inklap-header: chevron + titel + badge, en de actie-knop rechts
   zichtbaar zolang de kaart open staat. */
function InklapbareKaart({ titel, icon: Icon, badge, actie, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <button type="button" onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 flex-1 text-left group">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <CardTitle className="text-base flex items-center gap-2 group-hover:text-acid transition-colors">
            {Icon && <Icon className="h-4 w-4" />} {titel}
            {badge != null && (
              <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">· {badge}</span>
            )}
          </CardTitle>
        </button>
        {open && actie}
      </CardHeader>
      {open && <CardContent className="space-y-4">{children}</CardContent>}
    </Card>
  )
}

/* Duidelijke groepskop voor een set schermen/presets — icoon + accentbalk
   links, contextuele ondertitel. Bewust visueler dan de mono-caption zodat
   de scheiding deelnemer-vs-admin niet meer per ongeluk gemist wordt. */
function SchermGroep({ icon: Icon, kleur = '#B4FF39', titel, ondertitel, aantal,
                       extraTop = false, children }) {
  return (
    <div className={extraTop ? 'mt-6 pt-6 border-t border-border/40' : ''}>
      <div className="flex items-center gap-3 mb-3 pl-3"
           style={{ borderLeft: `4px solid ${kleur}` }}>
        {Icon && <Icon className="h-5 w-5" style={{ color: kleur }} />}
        <div className="flex-1">
          <div className="text-sm font-semibold" style={{ color: kleur }}>
            {titel} {aantal != null && <span className="text-muted-foreground font-normal ml-1">· {aantal}</span>}
          </div>
          {ondertitel && (
            <div className="text-[11px] text-muted-foreground leading-tight">{ondertitel}</div>
          )}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

/* Groep van presets met kolom-dropdown per stuk. */
function PresetGroep({ titel, icon: Icon, kleur = '#B4FF39', ondertitel,
                       extraTop = false, presets, onKolom, onRijen, onEdit, onWis }) {
  return (
    <div className={extraTop ? 'mt-5 pt-5 border-t border-border/40' : ''}>
      <div className="flex items-center gap-3 mb-3 pl-3"
           style={{ borderLeft: `4px solid ${kleur}` }}>
        {Icon && <Icon className="h-4 w-4" style={{ color: kleur }} />}
        <div className="flex-1">
          <div className="text-sm font-semibold" style={{ color: kleur }}>
            {titel} <span className="text-muted-foreground font-normal ml-1">· {presets.length}</span>
          </div>
          {ondertitel && (
            <div className="text-[11px] text-muted-foreground leading-tight">{ondertitel}</div>
          )}
        </div>
      </div>
      {presets.length === 0 && <p className="text-xs text-muted-foreground">Nog geen inhoud in deze groep.</p>}
      <div className="grid gap-1.5">
        {presets.map(p => {
          const Icon = p.Icon
          return (
            <div key={p.id}
                 className="flex items-center gap-2 rounded-[4px] border border-border bg-card px-3 py-1.5 flex-wrap"
                 style={{ borderLeft: `3px solid ${p.color}` }}>
              <Icon className="h-4 w-4 shrink-0" style={{ color: p.color }} />
              <span className="font-semibold text-sm flex-1 min-w-[8rem] truncate">{p.label}</span>
              <span className="font-mono text-[10px] text-muted-foreground truncate hidden sm:block max-w-[14rem]">{p.route}</span>
              <select value={p.midi_col ?? ''} onChange={e => onKolom(p, e.target.value)}
                      className={`${selectCls} w-auto min-w-[6rem]`}>
                <option value="">Geen kol.</option>
                {[0,1,2,3,4,5,6,7].map(n => <option key={n} value={n}>Kolom {n + 1}</option>)}
              </select>
              <RowChips selected={p.midi_rows ?? []}
                        onChange={(rows) => onRijen(p, rows)} compact />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(p)}>
                <Settings className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onWis(p.id)}>
                <Trash className="h-3.5 w-3.5 text-signal" />
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* Eén scherm-rij met alle bediening. */
function ScreenRow({ screen, presets, onSetRoute, onEdit, onDelete, onCopy, copied,
                     onToggleRotation, onKind, onRow }) {
  const current = presets.find(p => p.route === screen.current_route)
  const kind = screen.kind || 'participant'
  const kindKleur = schermKleur(screen)
  const knoppen = [...presets, { ...ZWART, Icon: Ban, color: '#94A3B8' }]

  return (
    <div className="rounded-[4px] border border-border bg-card p-3"
         style={{ borderLeft: `2px solid ${screen.is_online ? '#61E294' : '#94A3B8'}` }}>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <MonitorPlay className={`h-5 w-5 shrink-0 ${screen.is_online ? 'text-live' : 'text-muted-foreground'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{screen.name}</span>
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest"
                  style={{ color: kindKleur }}>
              {kind === 'admin' ? 'admin' : 'deelnemer'}
            </span>
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {screen.is_online
                ? <><Wifi className="h-3 w-3 text-live" /><span className="text-live">online</span></>
                : <><WifiOff className="h-3 w-3" />offline</>}
            </span>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            /scherm/{screen.slug}{screen.note ? ` · ${screen.note}` : ''}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Toont nu: <span className="text-foreground">{current?.label ?? (screen.current_route || 'niets')}</span>
            {screen.rotation_active && (
              <span className="text-live"> · wisselt elke {screen.rotation_seconds ?? 30}s</span>
            )}
          </div>
        </div>
        <select value={kind} onChange={e => onKind(screen, e.target.value)} className={`${selectCls} w-auto`}>
          <option value="participant">Deelnemer</option>
          <option value="admin">Admin</option>
        </select>
        <RowChips selected={screen.midi_rows ?? (screen.midi_row != null ? [screen.midi_row] : [])}
                  onChange={(next) => onRow(screen, next)} />

        <Button variant="ghost" size="sm" onClick={onCopy} title="Kopieer scherm-URL">
          {copied ? <Check className="h-4 w-4 text-live" /> : <Copy className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="sm" asChild title="Open dit scherm in een nieuw tabblad">
          <a href={`/scherm/${screen.slug}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onToggleRotation(screen, !screen.rotation_active)}
                title={screen.rotation_active ? 'Auto-switch uitzetten' : 'Auto-switch aanzetten'}>
          <RotateCw className={`h-4 w-4 ${screen.rotation_active ? 'text-live' : ''}`} />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}><Edit3 className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={onDelete}><Trash2 className="h-4 w-4 text-signal" /></Button>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {knoppen.map(p => {
          const Icon = p.Icon
          const actief = screen.current_route === p.route
          return (
            <button
              key={p.label + p.route}
              onClick={() => onSetRoute(screen.id, p.route)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] border text-xs transition-colors ${
                actief ? 'border-acid bg-acid/10 text-acid' : 'border-border text-muted-foreground hover:border-acid/40 hover:text-foreground'
              }`}
            >
              <Icon className="h-3.5 w-3.5" style={{ color: actief ? undefined : p.color }} />
              {p.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* Multi-select rij-chips (0-7). Klik een cijfer om te togglen. Compact-modus
   gebruikt kleinere padding voor de preset-rij (waar minder ruimte is). */
function RowChips({ selected = [], onChange, compact = false }) {
  const set = new Set((selected || []).map(Number))
  const toggle = (n) => {
    const nieuw = new Set(set)
    if (nieuw.has(n)) nieuw.delete(n); else nieuw.add(n)
    onChange?.([...nieuw].sort((a, b) => a - b))
  }
  return (
    <div className={`inline-flex items-center gap-1 rounded-[4px] border border-border bg-background ${compact ? 'p-0.5' : 'p-1'}`}
         title="Klik om rijen aan/uit te zetten. Meerdere selecties = scherm/preset zit op meerdere APC-rijen.">
      {[0,1,2,3,4,5,6,7].map(n => {
        const on = set.has(n)
        return (
          <button key={n} type="button" onClick={() => toggle(n)}
                  className={`${compact ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-xs'} rounded font-mono font-semibold transition-colors ${
                    on ? 'bg-acid text-black' : 'text-muted-foreground hover:bg-accent'}`}>
            {n + 1}
          </button>
        )
      })}
    </div>
  )
}
